/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (password.length < 6) { toast.error('Minimum 6 caracteres'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        toast.success('Mot de passe reinitialise');
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur reseau'); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <p className="text-red-600">Lien invalide. Veuillez refaire une demande de reinitialisation.</p>
            <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline block mt-4">Mot de passe oublie</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Nouveau mot de passe</CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-green-600 text-2xl">&#10003;</span>
              </div>
              <p className="text-sm text-slate-600">Votre mot de passe a ete reinitialise avec succes.</p>
              <Link to="/login" className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">Se connecter</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Nouveau mot de passe</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 6 caracteres" required data-testid="reset-password-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirmer le mot de passe</Label>
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirmer" required data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !password || !confirm} data-testid="reset-submit-btn">
                {loading ? 'Reinitialisation...' : 'Reinitialiser'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
